import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import { formatCurrency } from "../../utils/currency";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import DatePicker from "../../components/common/DatePicker";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import ErrorAlert from "../../components/common/ErrorAlert";
import alertService from "../../utils/alertService";
import vendorService from "../../services/vendorService";
import currencyUomService from "../../services/currencyUomService";
import stockCategoryService from "../../services/stockCategoryService";
import blContainerService from "../../services/blContainerService";
import { useMasterModals } from "../../hooks/useMasterModals";

import { MAX_FILE_SIZE, MAX_FILE_SIZE_MB } from "../../utils/constants";
import { validateFileSize, validateFileType } from "../../utils/validators";
import TextArea from "../../components/common/TextArea";

const MultiFilePreviewField = ({
  label,
  value = [],
  onChange,
  error,
  max = 3,
  accept = ".pdf",
  disabled = false,
}) => {
  const files = Array.isArray(value) ? value : value ? [value] : [];

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (files.length + selectedFiles.length > max) {
      alertService.warning(`You can only upload a maximum of ${max} PDFs.`);
      const allowedCount = max - files.length;
      if (allowedCount > 0) {
        onChange([...files, ...selectedFiles.slice(0, allowedCount)]);
      }
    } else {
      onChange([...files, ...selectedFiles]);
    }
  };

  const removeFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    onChange(updatedFiles);
  };

  return (
    <div className="space-y-2 col-span-full">
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
          {label}
        </label>
        <span className="text-[9px] font-black text-slate-400 uppercase">
          {files.length} / {max} Files
        </span>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {files.map((file, idx) => {
          const isFile = file instanceof File;
          const fileName = isFile ? file.name : file.split("/").pop();
          const fileUrl = isFile ? URL.createObjectURL(file) : file;

          return (
            <div
              key={idx}
              className="flex items-center gap-2 p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm transition-all hover:shadow-md"
            >
              <div className="flex-1 truncate pl-1 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                {fileName}
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-8 w-8 flex items-center justify-center rounded-lg bg-white dark:bg-slate-700 text-primary-600 shadow-sm hover:text-primary-700 transition-all border border-slate-100 dark:border-slate-600"
                  title="View PDF"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </a>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="h-8 w-8 flex items-center justify-center rounded-lg bg-white dark:bg-slate-700 text-rose-500 shadow-sm hover:text-rose-600 transition-all border border-slate-100 dark:border-slate-600"
                    title="Remove File"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {!disabled && files.length < max && (
          <div className="relative group min-h-[44px]">
            <input
              type="file"
              multiple
              accept={accept}
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
            <div className="flex items-center justify-center h-full gap-3 px-4 py-2 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 group-hover:border-primary-500 transition-all">
              <svg
                className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              <span className="text-[11px] text-slate-500 dark:text-slate-400 font-black uppercase tracking-widest">
                Add PDF ({files.length}/{max})
              </span>
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="text-[10px] font-bold text-rose-500 uppercase tracking-tight mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

function PurchaseMasterFormPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id } = useEncryptedParams();
  const basePath = "/purchase-master";

  // --- State ---
  const [savedPmId, setSavedPmId] = useState(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [step2Errors, setStep2Errors] = useState({});
  const [initialContainerWeights, setInitialContainerWeights] = useState({});

  // --- Options ---
  const termsOfPaymentOptions = [
    { label: "15 Days", value: "15" },
    { label: "30 Days", value: "30" },
    { label: "45 Days", value: "45" },
  ];
  const [vendorOptions, setVendorOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [stockCategoryOptions, setStockCategoryOptions] = useState([]);
  const [swarnimYardId, setSwarnimYardId] = useState("");

  // --- Form Data ---
  const [purchaseData, setPurchaseData] = useState({
    billNo: "",
    billPDF: null,
    bookingNo: "",
    billDate: "",
    termsOfPayment: "",
    dueDate: "",
    netWeight: "",
    noOfContainers: "",
    vendorId: "",
    currencyId: "",
    dollarRate: "",
    memo: "",
    isActive: true,
    isConverted: false,
  });

  const [containerGroups, setContainerGroups] = useState([]);

  // --- Modal States ---
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [isStockCategoryModalOpen, setIsStockCategoryModalOpen] =
    useState(false);

  const isViewMode = pathname.includes("/view");
  const hasId = Boolean(id);
  const isEditPage = hasId && !isViewMode;
  const formMode = isViewMode ? "view" : (isEditPage ? "edit" : "create");

  // --- Initial Data Load ---
  useEffect(() => {
    let isMounted = true;
    const loadOptions = async () => {
      try {
        const [vendorsResult, currencies, categories, yardsResult] =
          await Promise.all([
            vendorService.getActiveVendors(),
            currencyUomService.getActiveCurrencies(),
            stockCategoryService.getActiveStockCategories(),
            apiClient.get("/yard/active"),
          ]);
        if (isMounted) {
          const vData =
            vendorsResult?.items || vendorsResult?.data || vendorsResult || [];
          setVendorOptions(
            vData.map((v) => ({
              label: v.vendorName || v.name,
              value: v.vendorId || v.id,
            })),
          );
          const cData =
            currencies?.items || currencies?.data || currencies || [];
          setCurrencyOptions(
            cData.map((c) => ({
              label: c.currencyCode,
              value: c.currencyUOMId,
            })),
          );
          const sData =
            categories?.items || categories?.data || categories || [];
          setStockCategoryOptions(
            sData.map((s) => ({
              label: s.stockCategoryName,
              value: s.stockCategoryId,
            })),
          );

          const yData = yardsResult?.data?.data || yardsResult?.data || [];
          const swarnim = yData.find(
            (y) => y.yardNo?.toUpperCase() === "SWARNIM",
          );
          if (swarnim) setSwarnimYardId(swarnim.yardId);
        }
      } catch (err) {
        console.error("Failed to load options", err);
      }
    };
    loadOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  // --- Fetch Vendors helper for modal success ---
  const fetchVendors = async () => {
    try {
      const vendorsResult = await vendorService.getActiveVendors();
      const vData =
        vendorsResult?.items || vendorsResult?.data || vendorsResult || [];
      setVendorOptions(
        vData.map((v) => ({
          label: v.vendorName || v.name,
          value: v.vendorId || v.id,
        })),
      );
    } catch (err) {
      console.error("Failed to load vendors", err);
    }
  };

  const fetchStockCategories = async () => {
    try {
      const categories = await stockCategoryService.getActiveStockCategories();
      const sData = categories?.items || categories?.data || categories || [];
      setStockCategoryOptions(
        sData.map((s) => ({
          label: s.stockCategoryName,
          value: s.stockCategoryId,
        })),
      );
    } catch (err) {
      console.error("Failed to load categories", err);
    }
  };

  // --- Load Edit Data ---
  useEffect(() => {
    if (!hasId || !id) return;
    let isMounted = true;
    const loadData = async () => {
      setIsLoadingEdit(true);
      try {
        const response = await apiClient.get(`/purchase-master/${id}`);
        const data = response?.data?.data || response?.data || {};
        if (isMounted) {
          setSavedPmId(data.purchaseMasterId);

          // Handle multi-file PDF parsing, prioritizing the billPDFUrls array
          let finalPDFs = data.billPDFUrls || [];

          if (finalPDFs.length === 0 && data.billPDF) {
            let parsedPDFs = [];
            try {
              parsedPDFs = JSON.parse(data.billPDF);
              if (!Array.isArray(parsedPDFs)) parsedPDFs = [data.billPDF];
            } catch (e) {
              parsedPDFs = Array.isArray(data.billPDF)
                ? data.billPDF
                : [data.billPDF];
            }

            finalPDFs = parsedPDFs
              .map((path) => {
                if (typeof path !== "string") return null;
                return path.startsWith("http")
                  ? path
                  : `${process.env.REACT_APP_API_URL || "http://localhost:8040/api/v1"}/../${path}`;
              })
              .filter(Boolean);
          }

          setPurchaseData({
            billNo: data.billNo || "",
            billPDF: finalPDFs,
            bookingNo: data.bookingNo || "",
            billDate: data.billDate ? data.billDate.split("T")[0] : "",
            termsOfPayment: data.termsOfPayment ? String(data.termsOfPayment) : "",
            dueDate: data.dueDate ? data.dueDate.split("T")[0] : "",
            netWeight: data.netWeight || "",
            noOfContainers: data.noOfContainers || "",
            vendorId: data.vendorId || "",
            currencyId: data.currencyId || "",
            dollarRate: data.dollarRate || "",
            memo: data.memo || "",
            isActive: data.isActive ?? true,
            isConverted: data.isConverted ?? false,
          });

          try {
            const contResponse = await apiClient.get(
              `/containers/pm/${data.purchaseMasterId}`,
              { params: { limit: 1000 } },
            );
            const loadedContainers = contResponse?.data?.data || [];

            // Store initial weights to handle weight reduction safely
            const initialWeights = {};
            loadedContainers.forEach((c) => {
              if (c.containerNo)
                initialWeights[c.containerNo.toUpperCase()] =
                  parseFloat(c.weight) || 0;
            });
            setInitialContainerWeights(initialWeights);

            const groups = [];
            for (const cont of loadedContainers) {
              const itemRes = await apiClient.get("/container-stock-details", {
                params: { blContainerId: cont.blContainerId, limit: 1000, page: 1 },
              });
              const root =
                itemRes?.data?.data?.data || itemRes?.data?.data || [];

              const group = {
                containerId: cont.blContainerId,
                containerNo: cont.containerNo || "",
                items: [],
              };

              if (Array.isArray(root) && root.length > 0) {
                group.items = root.map((s) => {
                  const netWeight = parseFloat(s.netWeight) || 0;
                  const price = parseFloat(s.price) || 0;
                  const dollarRate = parseFloat(data.dollarRate) || 0;
                  return {
                    id: s.blContainerStockDetailsId,
                    stockCategoryId: s.stockCategoryId || "",
                    remarks: s.remarks || "",
                    netWeight: s.netWeight || "",
                    price: s.price || "",
                    totalPrice: ((netWeight * price) / 1000).toFixed(2),
                    priceInRS: (
                      (netWeight * price * dollarRate) /
                      1000
                    ).toFixed(2),
                    isActive: s.isActive ?? true,
                    isConverted: s.isConverted ?? false,
                  };
                });
              } else {
                group.items.push({
                  tempId: Date.now() + Math.random(),
                  stockCategoryId: "",
                  remarks: "",
                  netWeight: "",
                  price: "",
                  totalPrice: 0,
                  priceInRS: 0,
                  isActive: true,
                  isConverted: false,
                });
              }
              groups.push(group);
            }
            setContainerGroups(groups);
          } catch (err) {
            console.error("Nested loading failed", err);
          }
        }
      } catch (err) {
        setFormError(normalizeApiError(err).message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [id, hasId]);

  // --- Auto-init groups for Create mode ---
  useEffect(() => {
    if (isLoadingEdit) return;
    const targetCount = parseInt(purchaseData.noOfContainers) || 0;
    const currentCount = containerGroups.length;

    if (targetCount === currentCount) return;

    if (targetCount > currentCount) {
      // Add new groups
      const additionalCount = targetCount - currentCount;
      const newGroups = Array.from({ length: additionalCount }, (_, i) => ({
        tempId: Date.now() + currentCount + i + Math.random(),
        containerNo: "",
        items: [
          {
            tempId: Date.now() + currentCount + i + 100 + Math.random(),
            stockCategoryId: "",
            description: "",
            netWeight: "",
            price: "",
            totalPrice: 0,
            priceInRS: 0,
            isActive: true,
            isConverted: false,
          },
        ],
      }));
      setContainerGroups((prev) => [...prev, ...newGroups]);
    } else if (targetCount < currentCount) {
      // Remove excess groups from the end
      setContainerGroups((prev) => prev.slice(0, targetCount));
    }
  }, [purchaseData.noOfContainers, isLoadingEdit]);

  // --- Recalculate all items when dollarRate changes ---
  useEffect(() => {
    const rate = parseFloat(purchaseData.dollarRate) || 0;
    setContainerGroups((prevGroups) =>
      prevGroups.map((group) => ({
        ...group,
        items: group.items.map((item) => {
          const netWeight = parseFloat(item.netWeight) || 0;
          const price = parseFloat(item.price) || 0;
          return {
            ...item,
            priceInRS: ((netWeight * price * rate) / 1000).toFixed(2),
          };
        }),
      })),
    );
  }, [purchaseData.dollarRate]);

  // --- Auto-calculate Due Date ---
  useEffect(() => {
    if (purchaseData.billDate && purchaseData.termsOfPayment) {
      const days = parseInt(purchaseData.termsOfPayment, 10);
      if (!isNaN(days)) {
        const invoiceDate = new Date(purchaseData.billDate);
        if (!isNaN(invoiceDate.getTime())) {
          invoiceDate.setDate(invoiceDate.getDate() + days);
          const calculatedDate = invoiceDate.toISOString().split("T")[0];
          setPurchaseData((prev) => ({ ...prev, dueDate: calculatedDate }));
        }
      }
    }
  }, [purchaseData.billDate, purchaseData.termsOfPayment]);

  const getMinDueDate = () => {
    if (!purchaseData.billDate) return null;
    const billDate = new Date(purchaseData.billDate);
    if (isNaN(billDate.getTime())) return null;
    const minDate = new Date(billDate);
    minDate.setDate(minDate.getDate() + 1);
    return minDate;
  };

  // --- Handlers ---
  const appendNumeric = (fd, key, value) => {
    if (value === "" || value === null || value === undefined) return;
    fd.append(key, value);
  };

  const parseWeight = (val) => {
    if (typeof val === "number") return val;
    return parseFloat(String(val || "0").replace(/,/g, "")) || 0;
  };

  const updateContainerNo = (gIdx, value) => {
    const newGroups = [...containerGroups];
    newGroups[gIdx].containerNo = value.toUpperCase();
    setContainerGroups(newGroups);
  };

  const updateItem = (gIdx, iIdx, field, value) => {
    if (field === "netWeight" && value !== "" && parseFloat(value) < 0) return;

    if ((field === "netWeight" || field === "price") && value !== "") {
      if (!/^\d{0,9}(\.\d{0,2})?$/.test(value)) return;
    }

    const newGroups = [...containerGroups];
    const item = newGroups[gIdx].items[iIdx];
    item[field] = value;

    if (field === "netWeight" || field === "price") {
      const netWeight = parseFloat(item.netWeight) || 0;
      const price = parseFloat(item.price) || 0;
      const dollarRate = parseFloat(purchaseData.dollarRate) || 0;

      item.totalPrice = ((netWeight * price) / 1000).toFixed(2);
      item.priceInRS = ((netWeight * price * dollarRate) / 1000).toFixed(2);
    }
    setContainerGroups(newGroups);
  };

  const addItemToGroup = (gIdx) => {
    const newGroups = [...containerGroups];
    newGroups[gIdx].items.push({
      tempId: Date.now() + Math.random(),
      stockCategoryId: "",
      remarks: "",
      netWeight: "",
      price: "",
      totalPrice: 0,
      priceInRS: 0,
      isActive: true,
      isConverted: false,
    });
    setContainerGroups(newGroups);
  };

  const removeItemFromGroup = async (gIdx, iIdx) => {
    const item = containerGroups[gIdx].items[iIdx];
    const hasStockId = Boolean(item.id);

    if (hasStockId) {
      const result = await alertService.deleteConfirm("this item");
      if (!result.isConfirmed) return;
      try {
        await apiClient.delete(`/container-stock-details/${item.id}`);
        alertService.success("Item deleted successfully.");
      } catch (err) {
        alertService.error(normalizeApiError(err).message);
        return;
      }
    }
    const newGroups = [...containerGroups];
    newGroups[gIdx].items.splice(iIdx, 1);

    setContainerGroups(newGroups);
  };

  const removeContainerGroup = async (gIdx) => {
    const group = containerGroups[gIdx];
    const hasContainerId = Boolean(group.containerId);

    if (hasContainerId) {
      const result = await alertService.deleteConfirm(
        "this entire container and all its items",
      );
      if (!result.isConfirmed) return;
      try {
        for (const item of group.items) {
          if (item.id)
            await apiClient.delete(`/container-stock-details/${item.id}`);
        }
        await blContainerService.deleteBLContainer(group.containerId);
        alertService.success("Container and its items deleted successfully.");
      } catch (err) {
        alertService.error(normalizeApiError(err).message);
        return;
      }
    }
    const newGroups = [...containerGroups];
    newGroups.splice(gIdx, 1);
    setContainerGroups(newGroups);
  };

  const getFilteredStockCategoryOptions = (gIdx, currentItemId) => {
    // Get all selected categories in this container EXCEPT the one in the current row
    const selectedInCategory = containerGroups[gIdx].items
      .filter((item) => (item.id || item.tempId) !== currentItemId)
      .map((item) => String(item.stockCategoryId))
      .filter((id) => id);

    return stockCategoryOptions.filter(
      (opt) => !selectedInCategory.includes(String(opt.value)),
    );
  };

  const handleFinalSave = async () => {
    setFormError(null);
    setFieldErrors({});
    setStep2Errors({});

    // 1. Validate Purchase Details
    const errors = {};
    if (!purchaseData.billNo?.trim()) errors.billNo = "Bill Number is required";
    if (!purchaseData.billDate) errors.billDate = "Bill Date is required";
    if (!purchaseData.vendorId) errors.vendorId = "Vendor is required";
    if (!purchaseData.netWeight || parseFloat(purchaseData.netWeight) <= 0)
      errors.netWeight = "Please add the required net weight.";
    if (!purchaseData.bookingNo?.trim())
      errors.bookingNo = "Booking Number is required";
    if (
      !purchaseData.noOfContainers ||
      parseInt(purchaseData.noOfContainers, 10) <= 0
    )
      errors.noOfContainers = "Minimum 1 container required";
    if (!purchaseData.currencyId) errors.currencyId = "Currency is required";
    if (!purchaseData.dollarRate || parseFloat(purchaseData.dollarRate) <= 0)
      errors.dollarRate = "Valid Rate is required";
    if (purchaseData.billDate && purchaseData.dueDate) {
      const bDate = new Date(purchaseData.billDate);
      const dDate = new Date(purchaseData.dueDate);
      if (!isNaN(bDate.getTime()) && !isNaN(dDate.getTime()) && dDate <= bDate) {
        errors.dueDate = "Due Date must be after Bill Date";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // 2. Validate Container Groups & Items
    const cErrors = {};
    let hasValidationErrors = false;

    if (containerGroups.length === 0) {
      alertService.error("At least one container is required.");
      return;
    }

    containerGroups.forEach((group, gIdx) => {
      if (!group.containerNo?.trim()) {
        cErrors[`g${gIdx}-containerNo`] = "Container Number is required";
        hasValidationErrors = true;
      }
      if (group.items.length === 0) {
        alertService.error(
          `Container ${group.containerNo || gIdx + 1} must have at least one item.`,
        );
        hasValidationErrors = true;
      }
      group.items.forEach((item, iIdx) => {
        if (!item.stockCategoryId) {
          cErrors[`g${gIdx}-i${iIdx}-stockCategoryId`] = "Stock Category is required";
          hasValidationErrors = true;
        }
        if (!item.netWeight || parseFloat(item.netWeight) <= 0) {
          cErrors[`g${gIdx}-i${iIdx}-netWeight`] = "Net Weight is required";
          hasValidationErrors = true;
        }
        if (!item.price || parseFloat(item.price) <= 0) {
          cErrors[`g${gIdx}-i${iIdx}-price`] = "Price is required";
          hasValidationErrors = true;
        }
      });
    });

    if (hasValidationErrors) {
      setStep2Errors(cErrors);
      alertService.error("Please fill all required shipment details.");
      return;
    }

    // 3. Logic Validations
    const uniqueContainers = [
      ...new Set(
        containerGroups
          .map((g) => g.containerNo?.trim().toUpperCase())
          .filter((c) => c),
      ),
    ];
    const allowedContainerCount = parseInt(purchaseData.noOfContainers) || 0;

    if (uniqueContainers.length !== containerGroups.length) {
      alertService.error(
        "Duplicate container numbers detected. Each container must be unique.",
      );
      return;
    }

    if (uniqueContainers.length > allowedContainerCount) {
      alertService.error(
        `You have entered ${uniqueContainers.length} containers, but only ${allowedContainerCount} are allowed.`,
      );
      return;
    }

    const totalWeight = containerGroups.reduce(
      (sum, group) =>
        sum +
        group.items.reduce(
          (iSum, item) => iSum + (parseFloat(item.netWeight) || 0),
          0,
        ),
      0,
    );
    const primaryNetWeight = parseWeight(purchaseData.netWeight);

    if (totalWeight > primaryNetWeight) {
      alertService.error(
        `Total items weight (${totalWeight.toLocaleString()} KG) exceeds total purchase net weight (${primaryNetWeight.toLocaleString()} KG).`,
      );
      return;
    }

    setIsSubmitting(true);
    try {
      // --- ACTION 1: Save Purchase Master ---
      const pmFormData = new FormData();
      pmFormData.append("billNo", purchaseData.billNo);
      pmFormData.append("billDate", purchaseData.billDate);
      if (purchaseData.termsOfPayment) {
        pmFormData.append("termsOfPayment", purchaseData.termsOfPayment);
      }
      if (purchaseData.dueDate) {
        pmFormData.append("dueDate", purchaseData.dueDate);
      }
      pmFormData.append("bookingNo", purchaseData.bookingNo);
      pmFormData.append("isActive", purchaseData.isActive);
      pmFormData.append("memo", purchaseData.memo || "");
      appendNumeric(
        pmFormData,
        "netWeight",
        parseWeight(purchaseData.netWeight),
      );
      pmFormData.append("vendorId", String(purchaseData.vendorId));
      pmFormData.append("currencyId", String(purchaseData.currencyId));
      pmFormData.append("dollarRate", String(purchaseData.dollarRate));
      pmFormData.append("noOfContainers", String(purchaseData.noOfContainers));

      // Append all PDFs to FormData
      const currentPDFs = Array.isArray(purchaseData.billPDF)
        ? purchaseData.billPDF
        : [];

      currentPDFs.forEach((file) => {
        if (file instanceof File) {
          pmFormData.append("billPDF", file);
        }
      });

      // Send the list of remaining existing files to the backend
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:8040/api/v1";
      const remainingExisting = currentPDFs
        .filter((f) => typeof f === "string")
        .map((f) => {
          // Strip the base URL and '../' prefix we might have added in loadData
          if (f.startsWith(apiUrl)) {
            return f.split("/../").pop();
          }
          return f;
        });

      pmFormData.append("existingBillPDF", JSON.stringify(remainingExisting));

      let pmIdToUse = savedPmId || id;
      let pmResult;

      if (pmIdToUse) {
        pmResult = await apiClient.patch(
          `/purchase-master/${pmIdToUse}`,
          pmFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
      } else {
        pmResult = await apiClient.post("/purchase-master", pmFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        pmIdToUse = pmResult.data.data.purchaseMasterId;
        setSavedPmId(pmIdToUse);
      }

      // --- ACTION 2: Bulk Upsert Containers ---
      const containerPayload = containerGroups.map((group) => ({
        blContainerId: group.containerId || undefined,
        containerNo: group.containerNo.trim().toUpperCase(),
        pmId: Number(pmIdToUse),
        weight: parseFloat(
          group.items
            .reduce((sum, i) => sum + (parseFloat(i.netWeight) || 0), 0)
            .toFixed(2),
        ),
        isActive: true,
      }));

      // Sort for weight reduction safety
      containerPayload.sort((a, b) => {
        const initialA =
          initialContainerWeights[a.containerNo.toUpperCase()] || 0;
        const initialB =
          initialContainerWeights[b.containerNo.toUpperCase()] || 0;
        return a.weight - initialA - (b.weight - initialB);
      });

      const containerRes =
        await blContainerService.bulkUpsertBLContainers(containerPayload);
      const savedContainers =
        containerRes.data?.data || containerRes.data || containerRes;

      const containerIdMap = {};
      const containersArray = Array.isArray(savedContainers)
        ? savedContainers
        : [savedContainers];
      containersArray.forEach((sc) => {
        if (sc && sc.containerNo)
          containerIdMap[sc.containerNo.toUpperCase()] = sc.blContainerId;
      });

      // --- ACTION 3: Bulk Upsert Stock Details ---
      const itemPayloads = [];
      containerGroups.forEach((group) => {
        const cId = containerIdMap[group.containerNo.toUpperCase()];
        group.items.forEach((item) => {
          const p = {
            blContainerId: cId,
            yardId: swarnimYardId || null,
            stockCategoryId: item.stockCategoryId,
            transactionDate:
              purchaseData.billDate || new Date().toISOString().split("T")[0],
            netWeight: parseFloat(item.netWeight) || 0,
            price: parseFloat(item.price) || 0,
            totalPrice: parseFloat(item.totalPrice) || 0,
            priceInRS: parseFloat(item.priceInRS) || 0,
            remarks: item.remarks || item.description || "",
            isActive: item.isActive ?? true,
            isLocked: false,
          };
          if (item.id) p.blContainerStockDetailsId = item.id;
          itemPayloads.push(p);
        });
      });

      if (itemPayloads.length > 0) {
        await apiClient.post(
          "/container-stock-details/bulk-upsert",
          itemPayloads,
        );
      }

      alertService.success("Purchase saved successfully.");
      navigate(basePath);
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const currencies = await currencyUomService.getActiveCurrencies();
      const cData = currencies?.items || currencies?.data || currencies || [];
      setCurrencyOptions(
        cData.map((c) => ({
          label: c.currencyCode,
          value: c.currencyUOMId || c.id,
        })),
      );
    } catch (err) {
      console.error("Failed to load currencies", err);
    }
  };

  const { openModal, renderModals } = useMasterModals({
    vendor: fetchVendors,
    stockCategory: fetchStockCategories,
    currency: fetchCurrencies,
  });

  const sectionHeaderClass =
    "text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2";
  const labelClass =
    "text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1 block";

  const totalCombinedWeight = containerGroups.reduce(
    (sum, g) =>
      sum + g.items.reduce((iSum, i) => iSum + parseWeight(i.netWeight), 0),
    0,
  );
  const primaryNetWeight = parseWeight(purchaseData.netWeight);
  const isOverweight = totalCombinedWeight > primaryNetWeight;

  if (isLoadingEdit)
    return (
      <div className="p-10 text-center text-slate-500">Loading details...</div>
    );

  return (
    <div className="space-y-8 pb-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white">
            {isViewMode
              ? "View Purchase Shipment"
              : formMode === "edit"
                ? "Edit Purchase Shipment"
                : "Create Purchase Master"}
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            {isViewMode
              ? "View details of purchase shipment items below"
              : "Fill in purchase details and shipment items below"}
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate(basePath)}>
          Back to List
        </Button>
      </div>

      {formError && <ErrorAlert message={formError} />}

      {/* --- Section 1: Primary Purchase Details --- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all">
        <div className="flex items-center justify-between mb-6 border-b pb-3">
          <h2 className={sectionHeaderClass}>
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-[10px] text-white">
              1
            </span>
            Primary Purchase Details
          </h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          <Input
            label="Bill No"
            value={purchaseData.billNo}
            error={fieldErrors.billNo}
            onChange={(e) =>
              setPurchaseData({
                ...purchaseData,
                billNo: e.target.value.replace(/[^a-zA-Z0-9-]/g, ""),
              })
            }
            labelClassName={labelClass}
            maxLength={50}
            disabled={isViewMode}
          />
          <DatePicker
            label="Bill Date"
            maxDate={new Date()}
            value={purchaseData.billDate}
            error={fieldErrors.billDate}
            onChange={(date) =>
              setPurchaseData((prev) => {
                const updated = { ...prev, billDate: date };
                if (!date) {
                  updated.dueDate = "";
                  updated.termsOfPayment = "";
                } else if (prev.dueDate) {
                  const bDate = new Date(date);
                  const dDate = new Date(prev.dueDate);
                  if (!isNaN(bDate.getTime()) && !isNaN(dDate.getTime()) && dDate <= bDate) {
                    updated.dueDate = "";
                  }
                }
                return updated;
              })
            }
            labelClassName={labelClass}
            disabled={isViewMode}
          />
          <SearchableDropdown
            label="Mode/Terms of Payment"
            value={purchaseData.termsOfPayment}
            options={termsOfPaymentOptions}
            error={fieldErrors.termsOfPayment}
            onChange={(e) =>
              setPurchaseData({ ...purchaseData, termsOfPayment: e.target.value })
            }
            labelClassName={labelClass}
            disabled={isViewMode}
            placeholder="Select Terms of Payment"
          />
          <DatePicker
            label="Due Date"
            value={purchaseData.dueDate}
            error={fieldErrors.dueDate}
            onChange={(date) =>
              setPurchaseData({ ...purchaseData, dueDate: date })
            }
            labelClassName={labelClass}
            disabled={isViewMode || !purchaseData.billDate}
            minDate={getMinDueDate()}
          />
          <SearchableDropdown
            label="Vendor"
            labelClassName={labelClass}
            value={purchaseData.vendorId}
            options={vendorOptions}
            error={fieldErrors.vendorId}
            onAdd={() => openModal("vendor")}
            addLabel="Add New Vendor"
            onChange={(e) =>
              setPurchaseData({ ...purchaseData, vendorId: e.target.value })
            }
            disabled={isViewMode}
          />
          <Input
            label="Net Weight (KG)"
            type="number"
            min="0"
            value={purchaseData.netWeight}
            error={fieldErrors.netWeight}
            disabled={isViewMode || purchaseData.isConverted}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e" || e.key === "E") {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d{0,9}(\.\d{0,2})?$/.test(val)) {
                setPurchaseData({ ...purchaseData, netWeight: val });
              }
            }}
            labelClassName={labelClass}
          />
          <Input
            label="Booking No"
            value={purchaseData.bookingNo}
            error={fieldErrors.bookingNo}
            onChange={(e) =>
              setPurchaseData({ ...purchaseData, bookingNo: e.target.value })
            }
            labelClassName={labelClass}
            disabled={isViewMode}
          />
          <Input
            label="No of Containers"
            type="number"
            min="0"
            value={purchaseData.noOfContainers}
            error={fieldErrors.noOfContainers}
            disabled={isViewMode || isEditPage || purchaseData.isConverted}
            onChange={(e) =>
              setPurchaseData({
                ...purchaseData,
                noOfContainers: e.target.value,
              })
            }
            labelClassName={labelClass}
          />
          <SearchableDropdown
            label="Currency"
            value={purchaseData.currencyId}
            options={currencyOptions}
            error={fieldErrors.currencyId}
            onChange={(e) =>
              setPurchaseData({ ...purchaseData, currencyId: e.target.value })
            }
            onAdd={() => openModal("currency")}
            addLabel="Add New Currency"
            labelClassName={labelClass}
            disabled={isViewMode}
          />
          <Input
            label="Currency Rate"
            type="number"
            min="0"
            value={purchaseData.dollarRate}
            error={fieldErrors.dollarRate}
            onChange={(e) =>
              setPurchaseData({ ...purchaseData, dollarRate: e.target.value })
            }
            labelClassName={labelClass}
            disabled={isViewMode}
          />
        </div>
        <div className="mt-4 space-y-4">
          <TextArea
            label="Memo"
            name="memo"
            value={purchaseData.memo}
            error={fieldErrors.memo}
            disabled={isViewMode}
            onChange={(e) =>
              setPurchaseData({ ...purchaseData, memo: e.target.value })
            }
            labelClassName={labelClass}
            placeholder="Enter purchase notes or reminders here..."
            rows={8}
          />
        </div>
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          <MultiFilePreviewField
            label="Bill PDF"
            value={purchaseData.billPDF}
            error={fieldErrors.billPDF}
            disabled={isViewMode || purchaseData.isConverted}
            onChange={(files) => {
              // Basic validation for each file
              let combinedError = null;
              files.forEach(file => {
                if (file instanceof File) {
                  const sizeError = validateFileSize(file);
                  const typeError = validateFileType(file, ["application/pdf"]);
                  if (sizeError || typeError) combinedError = sizeError || typeError;
                }
              });

              setFieldErrors((prev) => ({ ...prev, billPDF: combinedError }));
              setPurchaseData({
                ...purchaseData,
                billPDF: files,
              });
            }}
          />
        </div>
      </div>

      {/* --- Section 2: Combined Shipment & Item Details --- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all">
        <div className="flex items-center justify-between mb-6 border-b pb-3">
          <h2 className={sectionHeaderClass}>
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-[10px] text-white">
              2
            </span>
            Combined Shipment & Item Details
          </h2>
          {!isViewMode && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setContainerGroups((prev) => [
                  ...prev,
                  {
                    tempId: Date.now() + Math.random(),
                    containerNo: "",
                    items: [
                      {
                        tempId: Date.now() + Math.random() + 100,
                        stockCategoryId: "",
                        remarks: "",
                        netWeight: "",
                        price: "",
                        totalPrice: 0,
                        priceInRS: 0,
                        isActive: true,
                        isConverted: false,
                      },
                    ],
                  },
                ]);
              }}
              disabled={purchaseData.isConverted}
            >
              + Add Container
            </Button>
          )}
        </div>

        <div className="space-y-8">
          {containerGroups.map((group, gIdx) => (
            <div
              key={group.containerId || group.tempId}
              className="rounded-xl border border-slate-100 bg-slate-50/30 p-4 dark:border-slate-800 dark:bg-slate-800/20"
            >
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-4">
                <div className="w-full md:w-64">
                  <Input
                    label={`Container #${gIdx + 1} Number`}
                    labelClassName={labelClass}
                    value={group.containerNo}
                    error={step2Errors[`g${gIdx}-containerNo`]}
                    onChange={(e) => updateContainerNo(gIdx, e.target.value)}
                    placeholder="e.g. ABCU1234567"
                    disabled={isViewMode || purchaseData.isConverted}
                  />
                </div>
                {!isViewMode && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => addItemToGroup(gIdx)}
                      disabled={purchaseData.isConverted}
                    >
                      + Add Stock
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => removeContainerGroup(gIdx)}
                      disabled={purchaseData.isConverted}
                    >
                      Remove Container
                    </Button>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm min-w-[1000px]">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 text-[10px] uppercase tracking-widest text-slate-500 font-black">
                      <th className="py-2 px-3 w-[220px]">Stock Category *</th>
                      <th className="py-2 px-3 w-[250px]">Description</th>
                      <th className="py-2 px-3 text-right w-[130px]">
                        Net Weight (KG) *
                      </th>
                      <th className="py-2 px-3 text-right w-[120px]">
                        Price/MT *
                      </th>
                      <th className="py-2 px-3 text-right w-[140px]">
                        Total Price
                      </th>
                      <th className="py-2 px-3 text-right w-[140px]">
                        Price (INR)
                      </th>
                      {!isViewMode && <th className="py-2 px-3 text-center w-[60px]">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item, iIdx) => (
                      <tr
                        key={item.id || item.tempId}
                        className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-white/50 transition-colors"
                      >
                        <td className="py-2 px-3">
                          <SearchableDropdown
                            value={item.stockCategoryId}
                            error={
                              step2Errors[`g${gIdx}-i${iIdx}-stockCategoryId`]
                            }
                            onChange={(e) =>
                              updateItem(
                                gIdx,
                                iIdx,
                                "stockCategoryId",
                                e.target.value,
                              )
                            }
                            options={getFilteredStockCategoryOptions(
                              gIdx,
                              item.id || item.tempId,
                            )}
                            disabled={isViewMode || item.isConverted}
                            onAdd={() => openModal("stockCategory")}
                            addLabel="Add New Category"
                          />
                        </td>
                        <td className="py-2 px-3 text-xs">
                          <input
                            className={`w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 outline-none dark:bg-slate-900 dark:border-slate-800 ${(item.isConverted || isViewMode) ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}`}
                            value={item.remarks}
                            onChange={(e) =>
                              updateItem(gIdx, iIdx, "remarks", e.target.value)
                            }
                            placeholder="Description..."
                            disabled={isViewMode || item.isConverted}
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            className={`w-full rounded-lg border bg-white px-3 py-1.5 text-right outline-none dark:bg-slate-900 dark:border-slate-800 ${step2Errors[`g${gIdx}-i${iIdx}-netWeight`] ? "border-rose-500" : "border-slate-200"} ${(item.isConverted || isViewMode) ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}`}
                            value={item.netWeight}
                            onChange={(e) =>
                              updateItem(
                                gIdx,
                                iIdx,
                                "netWeight",
                                e.target.value,
                              )
                            }
                            disabled={isViewMode || item.isConverted}
                          />
                          {step2Errors[`g${gIdx}-i${iIdx}-netWeight`] && (
                            <p className="mt-1 text-[10px] font-bold text-rose-500 uppercase tracking-tight">
                              {step2Errors[`g${gIdx}-i${iIdx}-netWeight`]}
                            </p>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            className={`w-full rounded-lg border bg-white px-3 py-1.5 text-right outline-none dark:bg-slate-900 dark:border-slate-800 ${step2Errors[`g${gIdx}-i${iIdx}-price`] ? "border-rose-500" : "border-slate-200"} ${(item.isConverted || isViewMode) ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}`}
                            value={item.price}
                            onChange={(e) =>
                              updateItem(gIdx, iIdx, "price", e.target.value)
                            }
                            disabled={isViewMode || item.isConverted}
                          />
                          {step2Errors[`g${gIdx}-i${iIdx}-price`] && (
                            <p className="mt-1 text-[10px] font-bold text-rose-500 uppercase tracking-tight">
                              {step2Errors[`g${gIdx}-i${iIdx}-price`]}
                            </p>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-slate-700 dark:text-slate-300">
                          {formatCurrency(
                            item.totalPrice,
                            currencyOptions.find(
                              (o) =>
                                String(o.value) ===
                                String(purchaseData.currencyId),
                            )?.label,
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-emerald-600">
                          {formatCurrency(item.priceInRS, "INR")}
                        </td>
                        {!isViewMode && (
                          <td className="py-2 px-3 text-center">
                            <button
                              type="button"
                              onClick={() => removeItemFromGroup(gIdx, iIdx)}
                              className="text-rose-400 hover:text-rose-600 p-1 disabled:opacity-20"
                              disabled={item.isConverted}
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142c-.097.35-.277.663-.532.9a2.002 2.002 0 01-1.47.6H7.862c-.548 0-1.07-.22-1.46-.6a1.996 1.996 0 01-.537-.9L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100/50 dark:bg-slate-800/50 font-black">
                    <tr>
                      <td
                        colSpan={2}
                        className="py-2 px-3 text-right text-[10px] uppercase tracking-wider text-slate-400"
                      >
                        Container Total:
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-primary-600">
                        {group.items
                          .reduce(
                            (sum, i) => sum + (parseFloat(i.netWeight) || 0),
                            0,
                          )
                          .toLocaleString()}{" "}
                        KG
                      </td>
                      <td className="py-2 px-3"></td>
                      <td className="py-2 px-3 text-right font-mono">
                        {formatCurrency(
                          group.items.reduce(
                            (sum, i) => sum + (parseFloat(i.totalPrice) || 0),
                            0,
                          ),
                          currencyOptions.find(
                            (o) =>
                              String(o.value) ===
                              String(purchaseData.currencyId),
                          )?.label,
                        )}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-emerald-600">
                        {formatCurrency(
                          group.items.reduce(
                            (sum, i) => sum + (parseFloat(i.priceInRS) || 0),
                            0,
                          ),
                          "INR",
                        )}
                      </td>
                      {!isViewMode && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ))}

          {containerGroups.length === 0 && (
            <div className="py-12 text-center rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800">
              <p className="text-slate-400 font-bold italic">
                No containers added. Click "Add Container" to begin.
              </p>
            </div>
          )}
        </div>

        {/* --- Error Message for Overweight --- */}
        {isOverweight && (
          <div className="mt-8 mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-200 dark:bg-rose-900/20 dark:border-rose-900/30 flex items-center gap-4 text-rose-600 dark:text-rose-400 animate-pulse">
            <div className="h-10 w-10 flex items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-600 shrink-0">
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-black uppercase tracking-tight mb-1">
                Weight Limit Exceeded
              </p>
              <p className="text-xs font-bold opacity-80 leading-relaxed">
                Total shipment weight ({totalCombinedWeight.toLocaleString()}{" "}
                KG) is greater than the primary Net Weight (
                {primaryNetWeight.toLocaleString()} KG).
              </p>
            </div>
          </div>
        )}

        {/* --- Grand Totals --- */}
        {containerGroups.length > 0 && (
          <div className="mt-6 p-5 rounded-2xl bg-primary-50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-900/30 flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 flex items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <span className="text-sm font-black uppercase tracking-tight text-slate-800 dark:text-white">
                Shipment Grand Total
              </span>
            </div>
            <div className="flex flex-wrap gap-8 items-center">
              <div className="text-right">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                  Weight (KG)
                </div>
                <div
                  className={`text-lg font-black ${isOverweight ? "text-rose-500" : "text-slate-800 dark:text-white"}`}
                >
                  {totalCombinedWeight.toLocaleString()} /{" "}
                  {primaryNetWeight.toLocaleString()}
                </div>
              </div>
              <div className="h-8 w-[1px] bg-slate-200 dark:bg-slate-700 hidden md:block"></div>
              <div className="text-right">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                  Total Price (
                  {currencyOptions.find(
                    (o) => String(o.value) === String(purchaseData.currencyId),
                  )?.label || "$"}
                  )
                </div>
                <div className="text-lg font-black text-slate-800 dark:text-white">
                  {formatCurrency(
                    containerGroups.reduce(
                      (sum, g) =>
                        sum +
                        g.items.reduce(
                          (iSum, i) => iSum + (parseFloat(i.totalPrice) || 0),
                          0,
                        ),
                      0,
                    ),
                    currencyOptions.find(
                      (o) =>
                        String(o.value) === String(purchaseData.currencyId),
                    )?.label,
                  )}
                </div>
              </div>
              <div className="h-8 w-[1px] bg-slate-200 dark:bg-slate-700 hidden md:block"></div>
              <div className="text-right">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                  Price (INR)
                </div>
                <div className="text-lg font-black text-emerald-600">
                  {formatCurrency(
                    containerGroups.reduce(
                      (sum, group) =>
                        sum +
                        group.items.reduce(
                          (iSum, item) =>
                            iSum + (parseFloat(item.priceInRS) || 0),
                          0,
                        ),
                      0,
                    ),
                    "INR",
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {!isViewMode && (
          <div className="flex justify-end gap-3 border-t pt-6 mt-6 border-slate-100 dark:border-slate-800">
            <Button
              size="sm"
              variant="primary"
              isLoading={isSubmitting}
              onClick={handleFinalSave}
              disabled={isOverweight}
              className="px-12 py-3 shadow-lg shadow-primary-500/20"
            >
              {formMode === "edit" ? "Update Purchase" : "Create Purchase"}
            </Button>
          </div>
        )}
      </div>

      {renderModals()}
    </div>
  );
}

export default PurchaseMasterFormPage;
